/* eslint-disable @typescript-eslint/no-explicit-any */
// src/services/sseService.ts - Complete implementation with timeout error detection
import { getAccessToken, getRefreshToken, setToken } from '@/utils/tokenStorage';
import { refreshToken as refreshAuthToken } from './authService';

// Add timeout error type
export interface TimeoutError extends Error {
  isTimeout: boolean;
  statusCode: number;
}

/**
 * Checks if an error is a timeout-related error (504 or chunked encoding timeout)
 */
const isTimeoutError = (error: any): boolean => {
  if ('isTimeout' in error && error.isTimeout) return true;
  
  // Check for various timeout/network error patterns
  const errorMessage = error?.message || String(error);
  const errorName = error?.name || '';
  
  // Add console logging for debugging
  console.log('Checking timeout error:', { 
    errorMessage, 
    errorName, 
    error: error 
  });
  
  const isTimeout = errorMessage.includes('ERR_INCOMPLETE_CHUNKED_ENCODING') ||
         errorMessage.includes('INCOMPLETE_CHUNKED_ENCODING') ||
         errorMessage.includes('net::ERR_INCOMPLETE_CHUNKED_ENCODING') ||
         errorMessage.includes('network error') ||
         errorMessage.includes('Network request failed') ||
         (errorName === 'TypeError' && errorMessage.includes('network error')) ||
         (errorName === 'TypeError' && errorMessage.includes('Failed to fetch')) ||
         errorMessage.toLowerCase().includes('timeout') ||
         errorMessage.toLowerCase().includes('timed out');
  
  console.log('Is timeout error:', isTimeout);
  return isTimeout;
};

/**
 * Creates a timeout error from a stream interruption
 */
const createStreamTimeoutError = (_originalError: any): TimeoutError => {
  const error = new Error('The request timed out during processing. Please try again.') as TimeoutError;
  error.isTimeout = true;
  error.statusCode = 504; // Treat as 504 for UI purposes
  return error;
};

/**
 * Creates a timeout error object
 */
const createTimeoutError = (statusCode: number, message: string): TimeoutError => {
  const error = new Error(message) as TimeoutError;
  error.isTimeout = true;
  error.statusCode = statusCode;
  return error;
};

/**
 * Handles the token refresh mechanism.
 */
const handleTokenRefresh = async (): Promise<string> => {
  const refreshTokenValue = getRefreshToken();
  if (refreshTokenValue) {
    try {
      const { access_token, refresh_token } = await refreshAuthToken(refreshTokenValue);
      setToken(access_token, refresh_token);
      window.dispatchEvent(new CustomEvent('tokenRefreshed', {
        detail: { access_token, refresh_token }
      }));
      return access_token;
    } catch (error) {
      window.dispatchEvent(new CustomEvent('tokenRefreshFail'));
      throw new Error('Token refresh failed. Please log in again.');
    }
  } else {
    throw new Error('No refresh token available. Please log in.');
  }
};

/**
 * Makes an SSE request with automatic token refresh handling and timeout error detection.
 * @param url The URL to request.
 * @param body The request body (for POST).
 * @param signal AbortSignal for request cancellation.
 * @param method HTTP method (usually POST for SSE with body).
 * @returns Promise<Response>
 */
export async function makeSSERequest(
  url: string,
  body: unknown,
  signal: AbortSignal,
  method: 'POST' = 'POST'
): Promise<Response> {
  const performRequest = async (token: string): Promise<Response> => {
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream',
    };
    if (method === 'POST') {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, {
      method,
      headers,
      body: method === 'POST' ? JSON.stringify(body) : undefined,
      signal,
    });

    if (response.status === 401) {
      const newAccessToken = await handleTokenRefresh();
      return performRequest(newAccessToken); // Retry with the new token
    }

    if (!response.ok) {
      let errorBodyText = `SSE request failed: ${response.status} ${response.statusText}`;
      
      // Check for timeout errors (504 Gateway Timeout)
      if (response.status === 504) {
        try {
          const errorData = await response.json();
          const timeoutMessage = errorData.detail || 'The request timed out. Please try again.';
          throw createTimeoutError(504, timeoutMessage);
        } catch (jsonError) {
          // If JSON parsing fails, use default timeout message
          throw createTimeoutError(504, 'The request timed out. Please try again.');
        }
      }
      
      // Handle other error statuses
      try {
        const errorData = await response.json();
        errorBodyText = errorData.detail || JSON.stringify(errorData) || errorBodyText;
      } catch (e) {
        // If response is not JSON, use the text or default message
        try {
            const textError = await response.text();
            errorBodyText = textError || errorBodyText;
        } catch (textErr) { /* Keep original errorBodyText */ }
      }
      throw new Error(errorBodyText);
    }
    return response;
  };

  let token = getAccessToken();
  if (!token) {
    // eslint-disable-next-line no-useless-catch
    try {
        token = await handleTokenRefresh();
    } catch (refreshError) {
        throw refreshError;
    }
  }
  return performRequest(token);
}

// --- OpenAI-like Stream Processing (for Simple Chatbots) ---
export interface OpenAIStreamEventHandlers {
  onStreamStart?: () => void;
  onChunk?: (textChunk: string) => void;
  onStreamEnd?: (finishReason?: string | null) => void;
  onError?: (error: Error | TimeoutError) => void;
  onTimeoutError?: (error: TimeoutError) => void; // New timeout-specific handler
}

/**
 * Processes an SSE stream that follows the OpenAI format (delta content chunks).
 */
export async function processOpenAIStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: OpenAIStreamEventHandlers,
  abortSignal?: AbortSignal
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let streamLogicallyEnded = false;
  let lastFinishReason: string | null = null;

  handlers.onStreamStart?.();

  const signalEnd = (error?: Error | TimeoutError) => {
    if (!streamLogicallyEnded) {
      streamLogicallyEnded = true;
      if (error) {
        // Check if it's a timeout error
        if ('isTimeout' in error && error.isTimeout) {
          handlers.onTimeoutError?.(error as TimeoutError);
        } else {
          handlers.onError?.(error);
        }
      } else {
        handlers.onStreamEnd?.(lastFinishReason);
      }
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (abortSignal?.aborted) throw new Error('Stream aborted by caller.');
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonData = line.substring(6).trim();
          if (jsonData === '[DONE]') {
            signalEnd();
            return;
          }
          // Handle specific backend error signal if it's not standard JSON
          if (line === 'data: MakMindInferenceError') {
             throw new Error('An error occurred during inference (MakMindInferenceError)');
          }
          try {
            const parsedEvent = JSON.parse(jsonData);
            // Check for structured error within the OpenAI-like stream
            if (parsedEvent.type === "execution_error" && parsedEvent.error) {
                throw new Error(typeof parsedEvent.error === 'string' ? parsedEvent.error : JSON.stringify(parsedEvent.error));
            }
            const choice = parsedEvent.choices?.[0];
            if (choice) {
              const contentChunk = choice.delta?.content;
              if (typeof contentChunk === 'string' && contentChunk.length > 0) {
                handlers.onChunk?.(contentChunk);
              }
              if (choice.finish_reason) {
                lastFinishReason = choice.finish_reason;
              }
            }
          } catch (e: any) {
             if (e instanceof Error && (e.message.includes("MakMindInferenceError") || e.message.includes("execution_error"))) {
                throw e;
             }
             if (jsonData) console.warn('Error parsing OpenAI stream JSON line:', jsonData, e);
          }
        }
      }
    }
    // If loop finishes due to `done` without `[DONE]` message
    signalEnd();
  } catch (error: any) {
    if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('Stream aborted by caller'))) {
      console.log('OpenAI stream processing aborted.');
      signalEnd();
    } else {
      console.error('Error processing OpenAI stream:', error);
      
      // Check if it's a timeout-related error
      if (isTimeoutError(error)) {
        const timeoutError = createStreamTimeoutError(error);
        signalEnd(timeoutError);
      } else {
        signalEnd(error instanceof Error ? error : new Error(String(error)));
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// --- Structured Event Stream Processing (for Chatflow/Workflow) ---
export interface StructuredSSEEvent {
  type: string;
  timestamp?: string | number;
  node_id?: string;
  node_label?: string;
  node_type?: string;
  final_output?: {
    response?: string; // Expected field for assistant's textual response
    [key: string]: any;
  };
  error?: string | { message: string; [key: string]: any };
  [key: string]: any;
}

export interface StructuredStreamEventHandlers {
  onStreamOpen?: () => void;
  onEvent?: (event: StructuredSSEEvent) => void;
  onStreamClose?: (error?: Error | TimeoutError) => void;
  onTimeoutError?: (error: TimeoutError) => void; // New timeout-specific handler
}

/**
 * Enhanced structured stream processing with timeout error handling and heartbeat support.
 */
export async function processStructuredStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: StructuredStreamEventHandlers,
  abortSignal?: AbortSignal
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let streamLogicallyEnded = false;

  handlers.onStreamOpen?.();

  const signalClose = (error?: Error | TimeoutError) => {
    if (!streamLogicallyEnded) {
      streamLogicallyEnded = true;
      
      // Add debugging
      console.log('Structured stream signalClose called with error:', error);
      console.log('Error has isTimeout property:', error && 'isTimeout' in error && (error as any).isTimeout);
      
      if (error && 'isTimeout' in error && (error as any).isTimeout) {
        console.log('Calling onTimeoutError handler');
        handlers.onTimeoutError?.(error as TimeoutError);
      } else {
        console.log('Calling onStreamClose handler');
        handlers.onStreamClose?.(error);
      }
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (abortSignal?.aborted) throw new Error('Stream aborted by caller.');
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      
      // Process all complete messages (separated by \n\n) in the buffer
      let separatorIndex;
      while ((separatorIndex = buffer.indexOf('\n\n')) >= 0) {
        const message = buffer.substring(0, separatorIndex);
        buffer = buffer.substring(separatorIndex + 2);

        // Handle both "data: {JSON}" and plain "{JSON}" formats
        let jsonStr = message.trim();
        if (jsonStr.startsWith('data: ')) {
          jsonStr = jsonStr.substring(5).trim();
        }

        if (jsonStr && (jsonStr.startsWith('{') && jsonStr.endsWith('}'))) {
          try {
            const eventData = JSON.parse(jsonStr) as StructuredSSEEvent;
            
            // Handle heartbeat events silently
            if (eventData.type === 'heartbeat') {
              // Optional: Log heartbeat for debugging
              // console.log('Received heartbeat for node:', eventData.node_id);
              continue; // Skip processing heartbeat events
            }
            
            handlers.onEvent?.(eventData);

            // Check for terminal events
            if (eventData.type === 'execution_completed') {
              signalClose();
              return;
            }
            if (eventData.type === 'execution_error') {
              let errorMessage = 'Workflow/Chatflow execution error.';
              if (typeof eventData.error === 'string') {
                errorMessage = eventData.error;
              } else if (eventData.error && typeof eventData.error.message === 'string') {
                errorMessage = eventData.error.message;
              } else if (eventData.error) {
                errorMessage = JSON.stringify(eventData.error);
              }
              signalClose(new Error(errorMessage));
              return;
            }
          } catch (e: any) {
            console.error('Error parsing structured SSE JSON:', jsonStr, e);
            // Continue processing other messages instead of failing completely
          }
        } else if (jsonStr) {
          console.warn('Received non-JSON message in structured stream:', jsonStr);
        }
      }
    }
    
    // If loop finishes due to `done` without a terminal event
    signalClose();
  } catch (error: any) {
     if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('Stream aborted by caller'))) {
      console.log('Structured stream processing aborted.');
      signalClose();
    } else {
      console.error('Error processing structured stream:', error);
      
      // Check if it's a timeout-related error
      if (isTimeoutError(error)) {
        const timeoutError = createStreamTimeoutError(error);
        signalClose(timeoutError);
      } else {
        signalClose(error instanceof Error ? error : new Error(String(error)));
      }
    }
  } finally {
    reader.releaseLock();
  }
}